import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { t } from '@shared/i18n';

/** Tolère les erreurs de saisie courantes dans Cloudflare : espaces, guillemets, « https:// » oublié, « / » final. */
function clean(value: string | undefined): string {
  return (value ?? '')
    .trim()
    .replace(/^["']|["']$/g, '')
    .trim();
}

function normalizeUrl(value: string): string {
  let v = clean(value);
  if (v && !/^https?:\/\//i.test(v)) v = `https://${v}`;
  return v.replace(/\/+$/, '').replace(/\/rest\/v1$/, '');
}

const url = normalizeUrl((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '');
const anonKey = clean(import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined);

// problème de configuration détecté au chargement ; le texte est produit à l'affichage (langue du site)
let configProblem: { kind: 'missing' } | { kind: 'invalid'; detail: string } | null = null;

/** Problème de configuration à afficher (null si tout va bien). */
export function configError(): string | null {
  if (!configProblem) return null;
  if (configProblem.kind === 'missing')
    return t(
      'VITE_SUPABASE_URL ou VITE_SUPABASE_ANON_KEY manquant dans Cloudflare Pages (Settings → Variables and secrets).',
      'VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is missing in Cloudflare Pages (Settings → Variables and secrets).',
    );
  return t(
    `Configuration Supabase invalide (${configProblem.detail}). Vérifie VITE_SUPABASE_URL dans Cloudflare Pages : elle doit ressembler à https://xxxx.supabase.co`,
    `Invalid Supabase configuration (${configProblem.detail}). Check VITE_SUPABASE_URL in Cloudflare Pages: it should look like https://xxxx.supabase.co`,
  );
}

function makeClient(): SupabaseClient | null {
  if (!url || !anonKey) {
    configProblem = { kind: 'missing' };
    return null;
  }
  try {
    return createClient(url, anonKey);
  } catch (e) {
    configProblem = { kind: 'invalid', detail: (e as Error).message };
    return null;
  }
}

/** null si le site n'est pas (bien) configuré : les pages publiques restent accessibles. */
export const supabase = makeClient();

export function db() {
  if (!supabase) throw new Error(configError() ?? t('Configuration manquante.', 'Missing configuration.'));
  return supabase;
}
